import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
/* eslint-disable no-shadow */

/* eslint-disable react-hooks-ssr/react-hooks-global-ssr */

import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { motion, useMotionValue } from 'framer-motion';
import { usePanels } from "./usePanels";
import { isColumnDimension } from "./Panel";
import { useMedia } from 'react-media';
import { usePrevious, useReducedMotion } from "../../hooks";
import { shouldRenderResizer, getAreaColumn, getAreaRow, isResizerAtEnd } from "./resize-helpers";
import { useRegisterFlipChild } from "../../hooks/useFlip";
import { FlipContext } from "../../hooks/useFlip/FlipContext"; // Todo: consider aligning these with theme breakpoints

var defaultBreakpoints = {
  md: '(min-width: 1080px)',
  lg: '(min-width: 1440px)'
};
/**
 * Panels
 */

export function Panels(_ref) {
  var _ref$breakpoints = _ref.breakpoints,
      breakpoints = _ref$breakpoints === void 0 ? defaultBreakpoints : _ref$breakpoints,
      children = _ref.children,
      template = _ref.template,
      className = _ref.className,
      gap = _ref.gap,
      getTransition = _ref.getTransition,
      banding = _ref.banding;
  var disableAnimations = useReducedMotion();

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      hasInteracted = _useState2[0],
      setHasInteracted = _useState2[1];

  var hasMountedRef = useRef(false);
  var gridRef = useRef(null);
  var pointerEvents = useMotionValue('auto');

  var _usePanels = usePanels({
    template: template
  }),
      dispatch = _usePanels.dispatch,
      stateRef = _usePanels.stateRef;

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      activeDimension = _useState4[0],
      setActiveDimension = _useState4[1]; // eslint-disable-next-line consistent-return


  useEffect(function () {
    if (activeDimension) {
      pointerEvents.set('none');
      return function () {
        pointerEvents.set('auto');
      };
    }
  }, [activeDimension, pointerEvents]); // Store this function in a ref so that its change doesn't
  // cause our layout animation effect to run.

  var getTransitionRef = useRef(getTransition);
  useEffect(function () {
    getTransitionRef.current = getTransition;
  }, [getTransition]);
  var prevTemplateRef = useRef(template);
  var flipChildren = useRegisterFlipChild();
  var matches = useMedia({
    queries: {
      md: breakpoints.md,
      lg: breakpoints.lg
    }
  });

  function getBreakpoint(m) {
    if (m.lg) return 'lg';
    if (m.md) return 'md';
    return 'sm';
  }

  var breakpoint = getBreakpoint(matches);
  var activeBreakpoint = stateRef.current[breakpoint];
  var prevSize = usePrevious(breakpoint);
  var breakpointRef = useRef(breakpoint);
  useEffect(function () {
    breakpointRef.current = breakpoint;
  }, [breakpoint]);
  /**
   * Commit CSS Grid template to the DOM
   */

  var commitState = useCallback(function () {
    return dispatch({
      type: 'COMMIT'
    });
  }, [dispatch]);
  /**
   * Handle rubber-banding during resize.
   *
   * We immediately update any non-0 value. When banding is 0,
   * we assume the user has released their drag and we
   * animate back to 0.
   */

  useEffect(function () {
    if (!banding) {
      return;
    }

    var colState = activeBreakpoint.columns,
        rowState = activeBreakpoint.rows;

    function onBand(dimension) {
      return function (v) {
        if (v === 0) {
          banding.start(_defineProperty({}, dimension, v));
          return;
        }

        banding.set(_defineProperty({}, dimension, v));
      };
    }

    var unsubCol = colState.offset.onChange(onBand('x'));
    var unsubRow = rowState.offset.onChange(onBand('y')); // eslint-disable-next-line consistent-return

    return function () {
      unsubCol();
      unsubRow();
    };
  }, [stateRef, banding, activeBreakpoint]);
  /**
   * Pan start
   *
   * On pan start, get a snapshot of the active dimension
   * for use during pan. This allows to compare "before"
   * and "after" states during a drag.
   */

  var onPanStart = useCallback(function (area, resizer) {
    if (!hasInteracted) {
      setHasInteracted(true);
    }

    var areas = template[breakpoint].areas;
    var isColumn = isColumnDimension(resizer);
    var resizerAtEnd = isResizerAtEnd(resizer);

    function startResizingColumns() {
      setActiveDimension('columns');
      var index = getAreaColumn(area, areas, resizerAtEnd);

      var _window$getComputedSt = window.getComputedStyle(gridRef.current),
          gridTemplateColumns = _window$getComputedSt.gridTemplateColumns;

      dispatch({
        type: 'PAN_START',
        payload: {
          dimension: 'columns',
          index: index,
          snapshot: parseTemplateString(gridTemplateColumns)
        }
      });
    }

    function startResizingRows() {
      setActiveDimension('rows');
      var index = getAreaRow(area, areas, resizerAtEnd);

      var _window$getComputedSt2 = window.getComputedStyle(gridRef.current),
          gridTemplateRows = _window$getComputedSt2.gridTemplateRows;

      dispatch({
        type: 'PAN_START',
        payload: {
          dimension: 'rows',
          index: index,
          snapshot: parseTemplateString(gridTemplateRows)
        }
      });
    }

    return isColumn ? startResizingColumns() : startResizingRows();
  }, [breakpoint, dispatch, hasInteracted, template]);
  /**
   * Pan
   */

  var onPan = useCallback(function (pan) {
    dispatch({
      type: 'PAN',
      payload: {
        dimension: stateRef.current[breakpoint].activeDimension,
        index: stateRef.current[breakpoint].activeIndex,
        pan: pan
      }
    });
    commitState();
  }, [breakpoint, commitState, dispatch, stateRef]);
  /**
   * Pan end
   */

  var onPanEnd = useCallback(function () {
    // Sometimes onPanEnd fires even if a pan start hasn't.
    // This appears to be a bug in framer-motion.
    if (!activeDimension) {
      return;
    }

    setActiveDimension(false);
    dispatch({
      type: 'PAN_END'
    });
  }, [activeDimension, dispatch]);
  /**
   * Handle media-query changes
   */

  useEffect(function () {
    dispatch({
      type: 'SET_ACTIVE_BREAKPOINT',
      payload: {
        breakpoint: breakpoint
      }
    });

    if (hasInteracted && prevSize && prevSize !== breakpoint) {
      dispatch({
        type: 'UPDATE',
        payload: {
          template: template
        }
      });
      commitState();
    }
  }, [breakpoint, commitState, dispatch, hasInteracted, prevSize, template]);
  /**
   * Toggle panel visibility
   */

  useEffect(function () {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    } // Only run this if our visible panels have actually changed


    if (prevTemplateRef.current.visible === template.visible) {
      return;
    }

    var registeredChildren = flipChildren.registeredChildren.current; // Measure children before layout changes

    registeredChildren.forEach(function (child) {
      return child.snapshotBefore && child.snapshotBefore();
    });
    dispatch({
      type: 'UPDATE',
      payload: {
        template: template
      }
    });
    commitState();
    gridRef.current.style.gridTemplateColumns = stateRef.current[breakpointRef.current].columns.template.get();
    gridRef.current.style.gridTemplateRows = stateRef.current[breakpointRef.current].rows.template.get(); // Measure children after layout changes

    registeredChildren.forEach(function (child) {
      return child.snapshotAfter && child.snapshotAfter();
    });
    var transition = getTransitionRef.current ? getTransitionRef.current(prevTemplateRef.current, template) : template.transition;

    if (disableAnimations) {
      transition = 'disabled';
    } // Tell children to animate between their "before" and "after" position.


    registeredChildren.forEach(function (child) {
      return child.animate && child.animate(null, {
        previousTemplate: prevTemplateRef.current,
        template: template,
        transitionType: transition
      });
    });
  }, [template, dispatch, stateRef, commitState, flipChildren.registeredChildren, disableAnimations]);
  useEffect(function () {
    prevTemplateRef.current = template;
  }, [template]);
  /**
   * When rendering on the server, we need to supply styles for all
   * three breakpoints. It's only after the user first manipulates
   * the panels that we switch to client-side.
   */

  var responsiveTemplateDefinition = useMemo(function () {
    var initialGridTemplate = stateRef.current;
    return css(["grid-template-columns:", ";grid-template-rows:", ";grid-template-areas:", ";@media ", "{grid-template-columns:", ";grid-template-rows:", ";grid-template-areas:", ";}@media ", "{grid-template-columns:", ";grid-template-rows:", ";grid-template-areas:", ";}"], initialGridTemplate.sm.columns.template.get(), initialGridTemplate.sm.rows.template.get(), initialGridTemplate.sm.areaTemplate.get(), breakpoints.md, initialGridTemplate.md.columns.template.get(), initialGridTemplate.md.rows.template.get(), initialGridTemplate.md.areaTemplate.get(), breakpoints.lg, initialGridTemplate.lg.columns.template.get(), initialGridTemplate.lg.rows.template.get(), initialGridTemplate.lg.areaTemplate.get());
  }, [breakpoints.lg, breakpoints.md, stateRef]);
  return /*#__PURE__*/React.createElement(FlipContext.Provider, {
    value: flipChildren
  }, /*#__PURE__*/React.createElement(StyledPanels, {
    className: className,
    ref: gridRef,
    $responsiveStyles: responsiveTemplateDefinition,
    style: hasInteracted && {
      gridTemplateRows: activeBreakpoint.rows.template,
      gridTemplateColumns: activeBreakpoint.columns.template,
      gridTemplateAreas: activeBreakpoint.areaTemplate,
      cursor: cursorForDimension(activeDimension)
    }
  }, React.Children.map(children, function (child) {
    if (React.isValidElement(child)) {
      var resizer = template[breakpoint].resizers[child.props.area];
      var renderResizer = shouldRenderResizer(child.props.area, template[breakpoint], resizer);
      return React.cloneElement(child, {
        pointerEvents: pointerEvents,
        onPanStart: onPanStart,
        onPan: onPan,
        onPanEnd: onPanEnd,
        resizer: renderResizer && resizer,
        gap: gap,
        isShowing: template.visible.includes(child.props.area),
        disableAnimations: disableAnimations
      });
    }

    return null;
  })));
}
var StyledPanels = styled(motion.div).withConfig({
  displayName: "Panels__StyledPanels",
  componentId: "vapor__dpqzw7-0"
})(["display:grid;width:100%;height:100%;position:relative;", ";"], function (p) {
  return p.$responsiveStyles;
});

function cursorForDimension(dimension) {
  switch (dimension) {
    case 'columns':
      return 'col-resize';

    case 'rows':
      return 'row-resize';

    default:
      return 'auto';
  }
}

function parseTemplateString(template) {
  return template.split(' ').map(function (v) {
    return Number(v.split('px')[0]);
  });
}